import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import {
  FREE_TRIAL_ATTENDEES_PER_STREAM,
  FREE_TRIAL_DAYS,
  FREE_TRIAL_STREAMS,
} from "@/lib/trial";
import { AuthShell } from "./auth-shell";

function ZoomMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.5 6.75A2.25 2.25 0 0 1 6.75 4.5h6A2.25 2.25 0 0 1 15 6.75v6a2.25 2.25 0 0 1-2.25 2.25h-6A2.25 2.25 0 0 1 4.5 12.75v-6Zm11.25 2.1 3.3-2.2a.75.75 0 0 1 1.2.6v7.5a.75.75 0 0 1-1.2.6l-3.3-2.2v-4.3Z"
      />
    </svg>
  );
}

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function MicrosoftMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M2.5 2.5h9v9h-9z" />
      <path fill="#7FBA00" d="M12.5 2.5h9v9h-9z" />
      <path fill="#00A4EF" d="M2.5 12.5h9v9h-9z" />
      <path fill="#FFB900" d="M12.5 12.5h9v9h-9z" />
    </svg>
  );
}

export function SignupPage() {
  const { user, organizations, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [zoomLoginEnabled, setZoomLoginEnabled] = useState(false);
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);
  const [microsoftLoginEnabled, setMicrosoftLoginEnabled] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (loading || user) return;
    nameRef.current?.focus();
  }, [loading, user]);

  useEffect(() => {
    if (loading || user) return;
    let cancelled = false;
    void Promise.all([
      api<{ enabled: boolean }>("/api/auth/zoom")
        .then((status) => {
          if (!cancelled) setZoomLoginEnabled(Boolean(status.enabled));
        })
        .catch(() => {
          if (!cancelled) setZoomLoginEnabled(false);
        }),
      api<{ enabled: boolean }>("/api/auth/google")
        .then((status) => {
          if (!cancelled) setGoogleLoginEnabled(Boolean(status.enabled));
        })
        .catch(() => {
          if (!cancelled) setGoogleLoginEnabled(false);
        }),
      api<{ enabled: boolean }>("/api/auth/microsoft")
        .then((status) => {
          if (!cancelled) setMicrosoftLoginEnabled(Boolean(status.enabled));
        })
        .catch(() => {
          if (!cancelled) setMicrosoftLoginEnabled(false);
        }),
    ]);
    return () => {
      cancelled = true;
    };
  }, [loading, user]);

  if (!loading && user) {
    const first = organizations[0]?.id;
    return <Navigate to={first ? "/" : "/onboarding"} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api<{ user: unknown; accountId: string }>("/api/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          displayName,
        }),
      });
      // No session until email is verified — prompt the user to check inbox.
      setVerificationSent(true);
      await refresh().catch(() => undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create account");
    } finally {
      setBusy(false);
    }
  }

  if (verificationSent) {
    return (
      <AuthShell
        title="Check your email"
        description={`We sent a verification link to ${email || "your inbox"}. Open it to finish creating your account, then sign in.`}
        belowPanel={
          <p className="text-center text-sm text-rs-ink-muted">
            Already verified?{" "}
            <Link
              to="/login"
              className="font-medium underline-offset-2 hover:underline"
            >
              Sign In
            </Link>
          </p>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-rs-ink-muted" role="status">
            The link expires in 24 hours. If you don&apos;t see it, check spam —
            or request a new link from the sign-in page.
          </p>
          <Button
            className="rs-auth-submit rs-auth-submit-block"
            type="button"
            onClick={() => navigate("/login")}
          >
            Go to Sign In
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create Your Account"
      description={`Start with ${FREE_TRIAL_STREAMS} sync streams for ${FREE_TRIAL_DAYS} days, up to ${FREE_TRIAL_ATTENDEES_PER_STREAM} attendees per stream.`}
      belowPanel={
        <p className="text-center text-sm text-rs-ink-muted">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium underline-offset-2 hover:underline"
          >
            Sign In
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Input
          ref={nameRef}
          id="displayName"
          name="displayName"
          autoComplete="name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your Name"
          aria-label="Your Name"
        />

        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          aria-label="Email"
        />

        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (8+ characters)"
            aria-label="Password"
            className="pr-10"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-slate-200"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide Password" : "Show Password"}
            aria-pressed={showPassword}
          >
            {showPassword ? (
              <EyeOff className="size-4" aria-hidden />
            ) : (
              <Eye className="size-4" aria-hidden />
            )}
          </button>
        </div>

        {error ? (
          <p className="text-sm text-rs-danger" role="alert">
            {error}
          </p>
        ) : null}

        <Button
          className="rs-auth-submit rs-auth-submit-block"
          type="submit"
          disabled={busy}
        >
          {busy ? "Creating Account…" : "Create Account"}
        </Button>
        {zoomLoginEnabled || googleLoginEnabled || microsoftLoginEnabled ? (
          <div className="rs-auth-alt mt-2">
            <div className="rs-auth-divider" role="separator" aria-label="or">
              <span>OR</span>
            </div>
            <div className="rs-auth-sso-row">
              {googleLoginEnabled ? (
                <a
                  href="/oauth/login/google/start"
                  className="rs-auth-sso"
                  aria-label="Sign Up with Google"
                  title="Sign Up with Google"
                >
                  <GoogleMark className="rs-auth-sso-icon" />
                  <span className="rs-auth-sso-label">Google</span>
                </a>
              ) : null}
              {microsoftLoginEnabled ? (
                <a
                  href="/oauth/login/microsoft/start"
                  className="rs-auth-sso"
                  aria-label="Sign Up with Microsoft"
                  title="Sign Up with Microsoft"
                >
                  <MicrosoftMark className="rs-auth-sso-icon" />
                  <span className="rs-auth-sso-label">Microsoft</span>
                </a>
              ) : null}
              {zoomLoginEnabled ? (
                <a
                  href="/oauth/login/start"
                  className="rs-auth-sso"
                  aria-label="Sign Up with Zoom"
                  title="Sign Up with Zoom"
                >
                  <ZoomMark className="rs-auth-sso-icon rs-auth-sso-icon-zoom" />
                  <span className="rs-auth-sso-label">Zoom</span>
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
      </form>
    </AuthShell>
  );
}
